import { useEffect, useRef, useState } from 'react';
import { cn } from '@divlab/divanui';

import useMedias from '@Hooks/useMedias';
import * as HeaderStore from '@Stores/Header';
import { useDeps } from '@Contexts/DI';
import Link from '@Navigation/Link';
import styles from './CollectionBenefit.module.css';

import type { CSSProperties, FC, HTMLAttributes } from 'react';
import type { CollectionBenefitData, CollectionBenefitText } from './typings';

interface CollectionBenefitsProps extends HTMLAttributes<HTMLDivElement> {
  data: CollectionBenefitData;
  text: CollectionBenefitText;
}

/*
  Все анимации с смещением блока и с изменением видимой части сменяемой картинки линейны,
  имеют вид y = kx + b
*/

/** Вычисляет значение из диапазона мин и макс значения */
const clamp = (min: number, value: number, max: number) => Math.min(Math.max(min, value), max);

/** Вычисляет угловой коэффициент функции (k) */
const calcSlopeCoef = ([x1, y1]: [number, number], [x2, y2]: [number, number]) => {
  return +((y2 - y1) / (x2 - x1)).toFixed(2);
};

/** Вычисляет свободный коэффициент функции (b) */
const calcFreeCoef = (x1: number, y1: number, k: number) => {
  return +(-k * x1 + y1).toFixed(2);
};

/** Вычисляет текущее значение анимации (y) по пограничным значениям прокрутки экрана (x) и анимации (y)
 *
 * @param currentX текущая прокрутка экрана
 * @param startX прокрутка экрана с которой начинается анимация
 * @param startY начальное значение анимации
 * @param endX прокрутка экрана на которой заканчивается анимация
 * @param endY конечное значение анимации
 * @returns текущее значение анимации
 */
const calcValue = (
  currentX: number,
  [startX, startY]: [number, number],
  [endX, endY]: [number, number],
) => {
  const k = calcSlopeCoef([startX, startY], [endX, endY]);
  const b = calcFreeCoef(startX, startY, k);

  const minY = Math.min(startY, endY);
  const maxY = Math.max(startY, endY);

  return +clamp(minY, k * currentX + b, maxY).toFixed(2);
};

const blockViewportHeightParts = 4.8; // Количество высот экрана для пролистывания анимаций
const screenPart = 0.4; // Часть экрана, которая будет рассчитываться для шага смены анимации

// Для смещения блока со сменяемым постельным бельем
const startBedLinensOffset = 100;
const endBedLinensOffset = 0;

// Для смещения блока с простыней
const startSheetOffset = 0;
const endSheetOffset = -200;

// Для изменения видимой части сменяемой картинки блока с постельным бельем
// Значения выходят из видимого диапазона [0, 100] для имитации паузы между анимациями
const startBedLinenTop = 120;
const endBedLinenTop = -20;

const CollectionBenefits: FC<CollectionBenefitsProps> = (props) => {
  const { className, data, text, ...restProps } = props;
  const { backgroundColor, textAlignment, images, link } = data;
  const { contentHeight } = HeaderStore.useSizes();
  const { isOnlyDesktop, isOnlyMobile } = useMedias();
  const { analytics } = useDeps();
  const blockRef = useRef<HTMLDivElement>(null);
  const backgroundVar = { '--background-color': backgroundColor } as CSSProperties;

  const [sheetOffset, setSheetOffset] = useState(startSheetOffset);

  const [bedLinensOffset, setBedLinensOffset] = useState(startBedLinensOffset);
  const [bedLinen2Top, setBedLinen2Top] = useState(startBedLinenTop);
  const [bedLinen3Top, setBedLinen3Top] = useState(startBedLinenTop);

  const [showBlobsGroup1, setShowBlobsGroup1] = useState(false);
  const [showBlobsGroup2, setShowBlobsGroup2] = useState(false);
  const [showBlobsGroup3, setShowBlobsGroup3] = useState(false);

  const [showText, setShowText] = useState<'first' | 'second'>('first');

  const handleClick = (label: string, targetPathname: string) => {
    analytics.dispatchEvent('catalog.open', {
      action: 'show',
      label: 'landingCategoryItems',
      secondaryLabel: label,
      targetPathname,
    });
  };

  useEffect(() => {
    const blockViewportHeight = isOnlyDesktop ? innerHeight - contentHeight : innerHeight;
    const blockHeight = blockViewportHeight * blockViewportHeightParts;
    const step = (blockViewportHeight / blockHeight) * screenPart * 100; // Шаг смены анимации в %

    function handleScroll() {
      if (!blockRef.current) return;
      const scrolledPx = -Math.round(blockRef.current.getBoundingClientRect().top); // Проскроллено вверх пикселей относительно окна браузера
      const scrolled = +((scrolledPx / blockHeight) * 100).toFixed(2); // Проскроллено вверх в % от высоты всего блока

      // 1. Смещение блока со сменяемым постельным бельем (1 вариант)
      if (-step <= scrolled && scrolled <= step) {
        const value = calcValue(
          scrolled,
          [-step, startBedLinensOffset],
          [step, endBedLinensOffset],
        );
        setBedLinensOffset(value);
      }
      if (scrolled > step) setBedLinensOffset(endBedLinensOffset);
      if (scrolled < -step) setBedLinensOffset(startBedLinensOffset);

      // 2. Смещение блока с простыней
      if (step <= scrolled && scrolled <= 4 * step) {
        const value = calcValue(scrolled, [step, startSheetOffset], [4 * step, endSheetOffset]);
        setSheetOffset(value);
      }
      if (scrolled < step) setSheetOffset(startSheetOffset);
      if (scrolled > 4 * step) setSheetOffset(endSheetOffset);

      // 2. Изменение видимой части картинки блока с постельным бельем (2 вариант)
      if (step <= scrolled && scrolled <= 4 * step) {
        const value = calcValue(scrolled, [step, startBedLinenTop], [4 * step, endBedLinenTop]);
        setBedLinen2Top(value);
      }
      if (scrolled < step) setBedLinen2Top(startBedLinenTop);
      if (scrolled > 4 * step) setBedLinen2Top(endBedLinenTop);

      // 3. Изменение видимой части картинки блока с постельным бельем (3 вариант)
      if (4 * step <= scrolled && scrolled <= 7 * step) {
        const value = calcValue(scrolled, [4 * step, startBedLinenTop], [7 * step, endBedLinenTop]);
        setBedLinen3Top(value);
      }
      if (scrolled < 4 * step) setBedLinen3Top(startBedLinenTop);
      if (scrolled > 7 * step) setBedLinen3Top(endBedLinenTop);

      // 4. Анимация смены текста
      if (scrolled >= 4 * step) setShowText('second');
      if (scrolled < 4 * step) setShowText('first');

      // 5. Анимация 1-ых цветочков
      if (scrolled >= 7 * step) setShowBlobsGroup1(true);
      if (scrolled <= 7.5 * step) setShowBlobsGroup1(false);

      // 6. Анимация 2-ых цветочков
      if (scrolled >= 7.5 * step) setShowBlobsGroup2(true);
      if (scrolled <= 8 * step) setShowBlobsGroup2(false);

      // 7. Анимация 3-ых цветочков
      if (scrolled >= 8 * step) setShowBlobsGroup3(true);
      if (scrolled <= 8.5 * step) setShowBlobsGroup3(false);
    }

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [contentHeight, isOnlyDesktop]);

  return (
    <div
      className={cn(styles.wrapper, className)}
      style={{
        height: `calc(${blockViewportHeightParts} * (100vh - ${contentHeight}px))`,
        backgroundColor,
      }}
      ref={blockRef}
      {...restProps}
    >
      <div
        className={cn(styles.container, { [styles.reverse]: textAlignment === 'left' })}
        style={{
          height: isOnlyDesktop ? `calc(100vh - ${contentHeight}px)` : '100vh',
          top: isOnlyDesktop ? `${contentHeight}px` : '0',
        }}
      >
        <div
          className={styles.imagesContainer}
          style={(isOnlyMobile && { height: `calc(100% - ${contentHeight}px)` }) || {}}
        >
          <img
            className={styles.sheet}
            src={images.sheet}
            style={{ transform: `translateY(${sheetOffset}%)` }}
          />
          <div
            className={styles.bedLinens}
            style={{ transform: `translateY(${bedLinensOffset}%)` }}
          >
            {images.bedLinens.map((bedLinen, index) => {
              let top = null;
              if (index === 1) top = bedLinen2Top;
              if (index === 2) top = bedLinen3Top;

              return (
                <img
                  key={bedLinen.src}
                  className={styles.bedLinen}
                  src={bedLinen.src}
                  style={{ clipPath: typeof top === 'number' ? `inset(${top}% 0 0)` : null }}
                />
              );
            })}

            {images.blobs.map((blob) => (
              <img
                key={blob.src}
                className={cn(styles.blob, {
                  [blob.rotate ? styles.visibleAndRotate : styles.visible]:
                    (blob.group === 1 && showBlobsGroup1) ||
                    (blob.group === 2 && showBlobsGroup2) ||
                    (blob.group === 3 && showBlobsGroup3),
                  [styles.behind]: blob.behind,
                })}
                src={blob.src}
                style={{
                  width: blob.width ? `${blob.width}%` : 'auto',
                  top: blob.top ? `${blob.top}%` : 'auto',
                  right: blob.right ? `${blob.right}%` : 'auto',
                  bottom: blob.bottom ? `${blob.bottom}%` : 'auto',
                  left: blob.left ? `${blob.left}%` : 'auto',
                }}
              />
            ))}
          </div>
        </div>
        <div className={styles.textContainer} style={backgroundVar}>
          {Array.from({ length: 2 }).map((_, index) => {
            const currentText = index ? text.second : text.first;

            return (
              <div
                key={currentText.title}
                className={cn(styles.text, {
                  [styles.visible]:
                    (showText === 'first' && index === 0) || (showText === 'second' && index === 1),
                })}
                style={{
                  background: `linear-gradient(180deg, #ffffff00 0%, ${backgroundColor}a2 20%)`,
                }}
              >
                <div
                  className={styles.title}
                  dangerouslySetInnerHTML={{ __html: currentText.title }}
                />
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{ __html: currentText.description }}
                />
                <Link
                  className={styles.link}
                  to={link}
                  view='primary'
                  onClick={() => handleClick(text.second.title, text.second.link)}
                >
                  {currentText.link}
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CollectionBenefits;
